/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  getAllOfficialTypes,
  getOfficialTypesForGeneric,
  getOfficialTypesForHockey,
  getOfficialTypesForSoccer,
  getTeamOfficialTypesBySportId,
} from '@sportninja/common/selectors/types'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { Select } from 'antd'
import type { BaseSelectRef } from 'rc-select'
import { forwardRef, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { paragraphMedium, weightRegular } from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import { ErrorLabel } from 'src/components/ts-components/ErrorLabel'

type OfficialType = {
  id: number
  name: string
  name_full: string
  is_default?: number
}

type RoleSelectProps = {
  // Redux props
  officialTypes: OfficialType[]
  teamOfficialTypes: OfficialType[]
  officialTypesForSoccer: OfficialType[]
  officialTypesForHockey: OfficialType[]
  officialTypesForGeneric: OfficialType[]
  // Component props
  name: string
  value: number | null
  onChange: (value: number) => void
  isTeamOfficialType?: boolean
  isSoccer?: boolean
  isOrg?: boolean
  sportId?: string | null
  disabled?: boolean
  required?: boolean
  fieldErrors?: any
  customError?: any
}

const RoleSelect = forwardRef<BaseSelectRef, RoleSelectProps>(
  (
    {
      officialTypes: initialOfficialTypes,
      teamOfficialTypes,
      officialTypesForSoccer,
      officialTypesForHockey,
      officialTypesForGeneric,
      name,
      value,
      onChange,
      isTeamOfficialType = false,
      isSoccer: _isSoccer = false,
      isOrg = false,
      disabled = false,
      required = false,
      fieldErrors,
      customError,
    },
    ref
  ) => {
    let officialTypes = initialOfficialTypes

    // For org level, combine all official types
    if (isOrg && !isCanlan) {
      const officialTypesForSoccerWithModifiedName = officialTypesForSoccer.map(
        (type) => ({
          ...type,
          name_full: type?.name_full,
          name: type?.name,
        })
      )
      const officialTypesForHockeyWithModifiedName = officialTypesForHockey.map(
        (type) => ({
          ...type,
          name_full: type?.name_full,
          name: type?.name,
        })
      )
      const officialTypesForGenericWithModifiedName =
        officialTypesForGeneric.map((type) => ({
          ...type,
          name_full: type?.name_full,
          name: type?.name,
        }))
      officialTypes = [
        ...officialTypesForSoccerWithModifiedName,
        ...officialTypesForHockeyWithModifiedName,
        ...officialTypesForGenericWithModifiedName,
      ]
    }

    const types = isTeamOfficialType ? teamOfficialTypes : officialTypes

    // Find the default type based on is_default property, fallback to first item
    const defaultTypeId = useMemo(() => {
      if (types.length === 0) {
        return undefined
      }
      const defaultItem = types.find((type) => type.is_default === 1)
      return defaultItem?.id ?? types[0]?.id
    }, [types])

    // Check if the current value is valid (exists in types)
    const isValueValid = useMemo(() => {
      if (!value) {
        return false
      }
      return types.some((type) => type.id === value)
    }, [value, types])

    // Set default value when types are loaded and no valid value exists
    useEffect(() => {
      if (types.length > 0 && !isValueValid && defaultTypeId !== undefined) {
        onChange(defaultTypeId)
      }
    }, [types.length, isValueValid, defaultTypeId, onChange])

    const options = useMemo(() => {
      return types.map((type) => ({
        value: type.id,
        label: type.name_full,
      }))
    }, [types])

    // Determine the displayed value - use value if valid, otherwise undefined (let Select show placeholder until default is set)
    const displayValue = isValueValid ? value : defaultTypeId

    const handleChange = (newValue: number) => {
      onChange(newValue)
    }

    // Use the same error detection pattern as TextInput
    const showError =
      fieldErrors?.[name] ||
      (Array.isArray(fieldErrors) && fieldErrors?.includes(name))

    return (
      <>
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.WHITE}
          style={`
          align-self: stretch;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        `}
        >
          Role{required ? ' *' : ''}
        </Text>
        <Select
          ref={ref}
          placeholder='Select a role'
          disabled={disabled}
          showSearch
          onChange={handleChange}
          options={options}
          value={displayValue}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          css={css`
            ${paragraphMedium}
            ${weightRegular}
          font-size: 14px !important;
            color: ${colors.WHITE};
            width: 100%;
            background: ${colors.SECONDARY_200} !important;
            border-radius: 8px !important;
            height: 40px !important;
            ${disabled && 'opacity: 0.5 !important'}
            .ant-select-selector {
              background: ${colors.SECONDARY_200} !important;
              border-radius: 8px !important;
              border: ${showError
                ? `1px solid ${colors.ERROR_200}`
                : ''} !important;
            }
            .ant-select-selection-item {
              font-size: 14px !important;
              font-weight: 400 !important;
              color: ${colors.WHITE} !important;
            }
          `}
          status={showError ? 'error' : ''}
        />
        <style>
          {`
          .ant-select-dropdown {
            z-index: 999999 !important;
          }
          .ant-select-dropdown .ant-select-item-option-content {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
        `}
        </style>
        <ErrorLabel
          fieldErrors={fieldErrors}
          customError={customError}
          name={name}
        />
      </>
    )
  }
)

const mapStateToProps = (
  state: any,
  { sportId }: { sportId?: string | null }
) => {
  return {
    officialTypes: getAllOfficialTypes(state),
    teamOfficialTypes: getTeamOfficialTypesBySportId(state, sportId),
    officialTypesForSoccer: getOfficialTypesForSoccer(state),
    officialTypesForHockey: getOfficialTypesForHockey(state),
    officialTypesForGeneric: getOfficialTypesForGeneric(state),
  }
}

export default connect(mapStateToProps)(RoleSelect)
